import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { List } from "../components/List";
import { ListButton } from "../components/ListButton";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { ListColumn } from "../components/ListColumn";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { InfoBox } from "../components/InfoBox";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../components/Table";
import {
  useVersionsInfiniteQuery,
  useBrandsQuery,
  useBrandsForVersionsQuery,
  useBundlesInfiniteQuery,
  useActivateBundleForAllBrandsMutation,
  useActivateBundleForAdminsAllBrandsMutation,
  useUpdateBrandConfigMutation,
} from "../query/bundlesQuery";
import type { RestBundle } from "@KassaroAB/shared/types/bundle";
import type { ActivateBundleForAllBrandsResult, Brand } from "../api/bundlesApi";
import { REFERENCE_BRAND } from "../api/bundlesApi";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const BUNDLE_VERSION_STORAGE_KEY = "selectedBundleVersionId";

type ActivationFeedback =
  | {
      kind: "bulk";
      scope: "all" | "admins";
      result: ActivateBundleForAllBrandsResult;
    }
  | {
      kind: "brand";
      scope: "all" | "admins" | "public";
      brandName: string;
    }
  | null;

const PageContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  padding: 0;
  max-width: 100%;
`;

const ColumnsContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

const InlineLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: ${spacing.x2}px;
`;

const VersionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const VersionRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing.x05}px;

  > button:first-child {
    flex: 1;
  }
`;

const ExpandButton = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  flex-shrink: 0;
  border: 1px solid ${({ $expanded }) => ($expanded ? colors.primary : colors.border)};
  border-radius: 4px;
  background-color: ${({ $expanded }) => ($expanded ? colors.background : colors.backgroundLight)};
  color: ${colors.text};
  cursor: pointer;
  padding: 0;

  &:hover {
    border-color: ${colors.primary};
  }
`;

const BrandList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  padding-left: ${spacing.x2}px;
`;

const NavItemContent = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.x1}px;
  width: 100%;
`;

const NavItemTitle = styled.span`
  font-weight: inherit;
`;

const NavItemMeta = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 400;
  opacity: 0.85;
  flex-shrink: 0;
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const CompactButton = styled(Button)`
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  white-space: nowrap;
`;

const ActionCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
  justify-content: flex-end;
`;

const DescriptionCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.x1}px;
  min-width: 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  line-height: 1.4;
`;

const StatusPill = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 999px;
  flex-shrink: 0;
  background-color: ${colors.successBackground};
  color: ${colors.success};
`;

const DescriptionText = styled.span`
  min-width: 0;
`;

const InlineEmpty = styled.div`
  padding: ${spacing.x2}px;
  text-align: center;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const formatBundleDescriptionText = (bundle: RestBundle): string => {
  const parts: string[] = [];

  if (bundle.gitHash) {
    parts.push(bundle.gitHash.slice(0, 7));
  }

  if (bundle.description) {
    parts.push(bundle.description);
  }

  return parts.join(" · ");
};

const readStoredVersionId = (): string | null => {
  if (typeof localStorage === "undefined") {
    return null;
  }
  return localStorage.getItem(BUNDLE_VERSION_STORAGE_KEY);
};

const findReferenceBrand = (brands: Brand[] | undefined): Brand | null => {
  if (!brands) {
    return null;
  }
  return (
    brands.find((brand) => brand.id === REFERENCE_BRAND || brand.name === REFERENCE_BRAND) ?? null
  );
};

const formatActiveBuildLabel = (brand: Brand | null): string => {
  if (!brand) {
    return "—";
  }

  const adminBuild = brand.activeBundleIdAdmin ?? null;
  const publicBuild = brand.activeBundleId ?? null;

  if (!adminBuild && !publicBuild) {
    return "—";
  }

  if (adminBuild && publicBuild) {
    if (adminBuild === publicBuild) {
      return adminBuild;
    }
    return `${publicBuild} (${adminBuild})`;
  }

  return publicBuild ?? adminBuild ?? "—";
};

export const BundlesView = () => {
  const { t } = useTranslation();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(() =>
    readStoredVersionId(),
  );
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(() =>
    readStoredVersionId(),
  );
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [activationFeedback, setActivationFeedback] = useState<ActivationFeedback>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false,
  );

  const {
    data: versionsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingVersions,
  } = useVersionsInfiniteQuery();

  const { data: expandedBrands, isLoading: isLoadingExpandedBrands } = useBrandsQuery(
    expandedVersionId,
  );
  const { data: selectedBrands } = useBrandsQuery(selectedVersionId);
  const {
    data: bundlesData,
    fetchNextPage: fetchNextBundlesPage,
    hasNextPage: hasMoreBundles,
    isFetchingNextPage: isFetchingMoreBundles,
    isLoading: isLoadingBundles,
  } = useBundlesInfiniteQuery(selectedVersionId, selectedBrandId);

  const bundles = bundlesData?.pages.flatMap((page) => page) ?? [];

  const {
    mutateAsync: activateBundleForAllBrands,
    isPending: isActivatingAllBrands,
  } = useActivateBundleForAllBrandsMutation();
  const {
    mutateAsync: activateBundleForAdminsAllBrands,
    isPending: isActivatingAdminsAllBrands,
  } = useActivateBundleForAdminsAllBrandsMutation();
  const { mutateAsync: updateBrandConfig, isPending: isUpdatingBrandConfig } =
    useUpdateBrandConfigMutation();

  const isActivating =
    isActivatingAllBrands ||
    isActivatingAdminsAllBrands ||
    isUpdatingBrandConfig;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = () => setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const allVersions = useMemo(
    () => versionsData?.pages.flatMap((page) => page) || [],
    [versionsData]
  );
  const versionIds = useMemo(() => allVersions.map((version) => version.id), [allVersions]);
  const brandsForVersionsQueries = useBrandsForVersionsQuery(versionIds);

  const brandsByVersionId = useMemo(() => {
    const map = new Map<string, Brand[]>();
    versionIds.forEach((versionId, index) => {
      const brandsForVersion = brandsForVersionsQueries[index]?.data;
      if (brandsForVersion) {
        map.set(versionId, brandsForVersion);
      }
    });
    return map;
  }, [versionIds, brandsForVersionsQueries]);

  const selectedVersion = allVersions.find((version) => version.id === selectedVersionId);
  const isBrandScope = selectedBrandId !== null;

  const selectedBrand = useMemo(() => {
    if (!selectedBrandId || !selectedBrands) {
      return null;
    }
    return selectedBrands.find((brand) => brand.id === selectedBrandId) ?? null;
  }, [selectedBrandId, selectedBrands]);

  const referenceBrand = useMemo(
    () => findReferenceBrand(selectedBrands),
    [selectedBrands],
  );

  const activeAdminBundleId = isBrandScope
    ? (selectedBrand?.activeBundleIdAdmin ?? null)
    : (referenceBrand?.activeBundleIdAdmin ?? null);

  const activePublicBundleId = isBrandScope
    ? (selectedBrand?.activeBundleId ?? null)
    : (referenceBrand?.activeBundleId ?? null);

  const showVersionsColumn = !isMobile || !selectedVersionId;
  const showBundlesColumn = !!selectedVersionId && (!isMobile || selectedVersionId);

  const persistVersionSelection = (versionId: string) => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(BUNDLE_VERSION_STORAGE_KEY, versionId);
    }
  };

  const handleToggleExpand = (versionId: string) => {
    setExpandedVersionId((current) => (current === versionId ? null : versionId));
  };

  const handleSelectAllBrands = (versionId: string) => {
    setSelectedVersionId(versionId);
    setExpandedVersionId(versionId);
    setSelectedBrandId(null);
    setActivationFeedback(null);
    persistVersionSelection(versionId);
  };

  const handleSelectBrand = (versionId: string, brandId: string) => {
    setSelectedVersionId(versionId);
    setExpandedVersionId(versionId);
    setSelectedBrandId(brandId);
    setActivationFeedback(null);
    persistVersionSelection(versionId);
  };

  const handleBundlesBack = () => {
    if (selectedBrandId) {
      setSelectedBrandId(null);
      setActivationFeedback(null);
      return;
    }
    setSelectedVersionId(null);
    setExpandedVersionId(null);
    setActivationFeedback(null);
  };

  const handleActivateBundleForAllBrands = (bundle: RestBundle) => {
    if (!selectedVersionId || !selectedVersion) {
      return;
    }

    const confirmed = window.confirm(
      t("bundlesView.activateAllConfirm", {
        build: bundle.buildNumber,
        version: selectedVersion.version,
      }),
    );

    if (!confirmed) {
      return;
    }

    activateBundleForAllBrands({
      versionId: selectedVersionId,
      bundleId: bundle.id,
    })
      .then((result) => {
        setActivationFeedback({ kind: "bulk", scope: "all", result });
      })
      .catch(() => undefined);
  };

  const handleActivateBundleForAdmins = (bundle: RestBundle) => {
    if (!selectedVersionId || !selectedVersion) {
      return;
    }

    const confirmed = window.confirm(
      t("bundlesView.activateAdminsConfirm", {
        build: bundle.buildNumber,
        version: selectedVersion.version,
      }),
    );

    if (!confirmed) {
      return;
    }

    activateBundleForAdminsAllBrands({
      versionId: selectedVersionId,
      bundleId: bundle.id,
    })
      .then((result) => {
        setActivationFeedback({ kind: "bulk", scope: "admins", result });
      })
      .catch(() => undefined);
  };

  const handleActivateBrandBundle = (
    bundle: RestBundle,
    scope: "admins" | "public" | "all",
  ) => {
    if (!selectedVersionId || !selectedBrandId || !selectedBrand) {
      return;
    }

    const confirmKey =
      scope === "admins"
        ? "bundlesView.activateBrandAdminsConfirm"
        : scope === "public"
          ? "bundlesView.activateBrandPublicConfirm"
          : "bundlesView.activateBrandAllConfirm";

    const confirmed = window.confirm(
      t(confirmKey, {
        build: bundle.buildNumber,
        brand: selectedBrand.name,
        version: selectedVersion?.version ?? "",
      }),
    );

    if (!confirmed) {
      return;
    }

    const data =
      scope === "admins"
        ? { activeBundleIdAdmin: bundle.id }
        : scope === "public"
          ? { activeBundleId: bundle.id }
          : { activeBundleId: bundle.id, activeBundleIdAdmin: bundle.id };

    updateBrandConfig({
      versionId: selectedVersionId,
      brandId: selectedBrandId,
      data,
    })
      .then(() => {
        setActivationFeedback({
          kind: "brand",
          scope,
          brandName: selectedBrand.name,
        });
      })
      .catch(() => undefined);
  };

  const renderFeedback = () => {
    if (!activationFeedback) {
      return null;
    }

    if (activationFeedback.kind === "bulk") {
      const { result, scope } = activationFeedback;
      return (
        <InfoBox variant={result.skippedBrands.length > 0 ? "warning" : "success"}>
          {result.skippedBrands.length > 0
            ? t(
                scope === "admins"
                  ? "bundlesView.activateAdminsPartial"
                  : "bundlesView.activateAllPartial",
                {
                  activated: result.activatedBrandIds.length,
                  skipped: result.skippedBrands.length,
                },
              )
            : t(
                scope === "admins"
                  ? "bundlesView.activateAdminsSuccess"
                  : "bundlesView.activateAllSuccess",
                {
                  count: result.activatedBrandIds.length,
                },
              )}
        </InfoBox>
      );
    }

    const messageKey =
      activationFeedback.scope === "admins"
        ? "bundlesView.activateBrandAdminsSuccess"
        : activationFeedback.scope === "public"
          ? "bundlesView.activateBrandPublicSuccess"
          : "bundlesView.activateBrandAllSuccess";

    return (
      <InfoBox variant="success">
        {t(messageKey, { brand: activationFeedback.brandName })}
      </InfoBox>
    );
  };

  const bundlesSubtitle = selectedVersion
    ? isBrandScope && selectedBrand
      ? t("bundlesView.bundlesBrandSubtitle", {
          version: selectedVersion.version,
          brand: selectedBrand.name,
        })
      : t("bundlesView.bundlesAllBrandsSubtitle", {
          version: selectedVersion.version,
        })
    : undefined;

  const renderNavItem = (title: string, meta: string) => (
    <NavItemContent>
      <NavItemTitle>{title}</NavItemTitle>
      <NavItemMeta>{meta}</NavItemMeta>
    </NavItemContent>
  );

  const renderBundleDescription = (
    bundle: RestBundle,
    isAdminActive: boolean,
    isPublicActive: boolean,
  ) => {
    const descriptionText = formatBundleDescriptionText(bundle);

    return (
      <DescriptionCell>
        {isAdminActive && <StatusPill>{t("bundlesView.pillAdmin")}</StatusPill>}
        {isPublicActive && <StatusPill>{t("bundlesView.pillPublic")}</StatusPill>}
        {descriptionText ? <DescriptionText>{descriptionText}</DescriptionText> : null}
      </DescriptionCell>
    );
  };

  const renderActions = (bundle: RestBundle) => {
    const isAdminActive = activeAdminBundleId === bundle.id;
    const isPublicActive = activePublicBundleId === bundle.id;

    if (isAdminActive && isPublicActive) {
      return null;
    }

    if (isBrandScope) {
      return (
        <ActionCell>
          {!isAdminActive && (
            <CompactButton
              $color="secondary"
              $variant="outlined"
              onClick={() => handleActivateBrandBundle(bundle, "admins")}
              disabled={isActivating}
            >
              {isActivating ? "…" : t("bundlesView.pillAdmin")}
            </CompactButton>
          )}
          {!isPublicActive && (
            <CompactButton
              $variant="outlined"
              onClick={() => handleActivateBrandBundle(bundle, "public")}
              disabled={isActivating}
            >
              {isActivating ? "…" : t("bundlesView.pillPublic")}
            </CompactButton>
          )}
        </ActionCell>
      );
    }

    return (
      <ActionCell>
        {!isAdminActive && (
          <CompactButton
            $color="secondary"
            $variant="outlined"
            onClick={() => handleActivateBundleForAdmins(bundle)}
            disabled={isActivating}
          >
            {isActivating ? "…" : t("bundlesView.pillAdmin")}
          </CompactButton>
        )}
        {!isPublicActive && (
          <CompactButton
            $variant="outlined"
            onClick={() => handleActivateBundleForAllBrands(bundle)}
            disabled={isActivating}
          >
            {isActivating ? "…" : t("bundlesView.pillPublic")}
          </CompactButton>
        )}
      </ActionCell>
    );
  };

  return (
    <PageContainer>
      <ColumnsContainer>
        {showVersionsColumn && (
          <ListColumn title={t("bundlesView.columnVersions")} width="240px" dense>
            {isLoadingVersions && allVersions.length === 0 ? (
              <InlineLoading>
                <LoadingSpinner />
              </InlineLoading>
            ) : allVersions.length === 0 ? (
              <InlineEmpty>{t("bundlesView.emptyVersions")}</InlineEmpty>
            ) : (
              <List dense>
                {allVersions.map((version) => {
                  const isExpanded = expandedVersionId === version.id;
                  const isAllBrandsSelected =
                    selectedVersionId === version.id && selectedBrandId === null;
                  const versionBrands = brandsByVersionId.get(version.id);
                  const referenceActiveLabel = formatActiveBuildLabel(
                    findReferenceBrand(versionBrands),
                  );
                  const brandsForExpanded =
                    isExpanded && expandedVersionId === version.id
                      ? (expandedBrands ?? versionBrands)
                      : versionBrands;

                  return (
                    <VersionBlock key={version.id}>
                      <VersionRow>
                        <ListButton
                          compact
                          isSelected={isAllBrandsSelected}
                          onClick={() => handleSelectAllBrands(version.id)}
                        >
                          {renderNavItem(`v${version.version}`, referenceActiveLabel)}
                        </ListButton>
                        <ExpandButton
                          type="button"
                          $expanded={isExpanded}
                          aria-expanded={isExpanded}
                          aria-label={t("bundlesView.toggleBrands")}
                          onClick={() => handleToggleExpand(version.id)}
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </ExpandButton>
                      </VersionRow>
                      {isExpanded && (
                        <BrandList>
                          {isLoadingExpandedBrands ? (
                            <InlineLoading>
                              <LoadingSpinner />
                            </InlineLoading>
                          ) : !brandsForExpanded || brandsForExpanded.length === 0 ? (
                            <InlineEmpty>{t("bundlesView.emptyBrands")}</InlineEmpty>
                          ) : (
                            brandsForExpanded.map((brand: Brand) => (
                              <ListButton
                                key={brand.id}
                                compact
                                isSelected={
                                  selectedVersionId === version.id &&
                                  selectedBrandId === brand.id
                                }
                                onClick={() => handleSelectBrand(version.id, brand.id)}
                              >
                                {renderNavItem(brand.name, formatActiveBuildLabel(brand))}
                              </ListButton>
                            ))
                          )}
                        </BrandList>
                      )}
                    </VersionBlock>
                  );
                })}
                <LoadMoreButton
                  onClick={() => fetchNextPage()}
                  isLoading={isFetchingNextPage}
                  hasMore={hasNextPage}
                />
              </List>
            )}
          </ListColumn>
        )}

        {showBundlesColumn && (
          <ListColumn
            title={t("bundlesView.bundles")}
            subtitle={bundlesSubtitle}
            flex
            dense
            onBack={isMobile ? handleBundlesBack : undefined}
          >
            {renderFeedback()}

            {isLoadingBundles && bundles.length === 0 ? (
              <InlineLoading>
                <LoadingSpinner />
              </InlineLoading>
            ) : bundles.length === 0 ? (
              <InlineEmpty>{t("bundlesView.emptyBundles")}</InlineEmpty>
            ) : (
              <>
                <TableCard>
                  <Table minWidth="100%">
                    <TableBody>
                      {bundles.map((bundle: RestBundle) => {
                        const isAdminActive = activeAdminBundleId === bundle.id;
                        const isPublicActive = activePublicBundleId === bundle.id;

                        return (
                          <TableRow key={bundle.id}>
                            <TableCell noWrap>
                              <strong>{bundle.buildNumber}</strong>
                            </TableCell>
                            <TableCell>
                              {renderBundleDescription(bundle, isAdminActive, isPublicActive)}
                            </TableCell>
                            <TableCell align="right">{renderActions(bundle)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableCard>
                <LoadMoreButton
                  onClick={() => fetchNextBundlesPage()}
                  isLoading={isFetchingMoreBundles}
                  hasMore={hasMoreBundles}
                />
              </>
            )}
          </ListColumn>
        )}
      </ColumnsContainer>
    </PageContainer>
  );
};
